import React, { useMemo } from 'react';
import {
  InputTable,
  type InputTableCellParams,
  type InputTableRowParams,
  type RepeatRowsPayload
} from '@/components/custom/erp';
import { DetailItemRow, SelectedCellInfo } from '../hooks/useDetailItemRows';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailItemColumns } from './columns';
import { useDetailItemPaste } from '../hooks';
import { FormMode } from '@/types/form';
import ActionBar from './ActionBar';

interface DetailItemTabProps {
  formMode: FormMode;
  rows: DetailItemRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams<DetailItemRow>) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<DetailItemRow>) => void;
  ma_ngv?: string;
  dien_giai?: string;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<unknown[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export const DetailItemTab: React.FC<DetailItemTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  ma_ngv,
  dien_giai,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  useInvoiceHydration({
    rows,
    enabled: formMode !== 'add',
    onHydrated: (uuid, invoice) => onCellValueChange(uuid, 'id_hd_data', invoice)
  });
  const columns = useMemo(
    () => getDetailItemColumns(onCellValueChange, ma_ngv, dien_giai, formMode),
    [onCellValueChange, ma_ngv, dien_giai, formMode]
  );

  useDetailItemPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
